<template>
  <div>
    <button
      type="button"
      class="text-black bg-white border md:border-none md:mt-4 md:me-0 hover:bg-blue-800 md:hover:bg-gray-100 hover:text-white md:hover:text-black focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-full text-lg p-2.5 text-center inline-flex items-center me-2 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
      @click="toggleOpen"
    >
      <i class="fa-regular fa-circle-info"></i>
    </button>
    <Modal
      v-if="open"
      no-footer
      title="Schedule Color Codes and Icons"
      @hide-modal="toggleOpen"
    >
      <div class="section-spacing-25">
        <div
          v-for="customization in statusCustomizations"
          :key="customization.id"
        >
          <div class="d-flex align-items-center">
            <div class="mr-3">
              <div
                class="color-pill mr-3"
                :style="{
                  background: customization.value || customization.default
                }"
              ></div>
            </div>
            <div>
              <div class="text-title">
                {{ statusMap[customization.key]?.label }}
              </div>
              <div class="text-body">
                {{ statusMap[customization.key]?.description }}
              </div>
            </div>
          </div>
        </div>

        <div>
          <div class="d-flex align-items-center">
            <div class="mr-3 flex rounded overflow-hidden">
              <TravelBG class="h-6" />
              <TravelBG class="h-6" />
            </div>
            <div>
              <div class="text-title">Travel Time</div>
              <div class="text-body">
                Technician is traveling to the job site.
              </div>
            </div>
          </div>
        </div>
        <div>
          <div class="d-flex align-items-center">
            <div class="mr-3 flex rounded overflow-hidden">
              <LateBG class="h-6" />
              <LateBG class="h-6" />
            </div>
            <div>
              <div class="text-title">Late</div>
              <div class="text-body">
                Technician finished their trip after the arrival window.
              </div>
            </div>
          </div>
        </div>
        <div>
          <div class="d-flex align-items-center">
            <div class="mr-3">
              <div class="color-pill hold mr-3"></div>
            </div>
            <div>
              <div class="text-title">Hold</div>
              <div class="text-body">
                A block of time reserved by the Scheduler for an unasigned
                appointment to run later in the same workday.
              </div>
            </div>
          </div>
        </div>
        <div>
          <div class="d-flex align-items-center">
            <div class="mr-3">
              <div class="color-pill icon-pill mr-3">
                <i class="far fa-lock-keyhole" />
              </div>
            </div>
            <div>
              <div class="text-title">Pinned (Lock icon)</div>
              <div class="text-body">
                Manually assigned to a specific Technician to be worked and will
                not be replanned by the scheduler.
              </div>
            </div>
          </div>
        </div>
        <div>
          <div class="d-flex align-items-center">
            <div class="mr-3">
              <div class="color-pill icon-pill mr-3">
                <i class="far fa-user-large" />
              </div>
            </div>
            <div>
              <div class="text-title">Customer Scheduled (Person icon)</div>
              <div class="text-body">
                Scheduled by the Customer (through the widget or customer
                portal).
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script setup lang="ts">
import { computed, ComputedRef, ref } from "vue";
import { useStore } from "vuex";

import Modal from "@/components/Modal.vue";
import { CustomizationRecord } from "@/Customizations/v2/customizations";
import LateBG from "@/images/late_travel_path.svg?component";
import TravelBG from "@/images/travel_path.svg?component";

const statusMap = {
  pending_unassigned: {
    label: "Pending (Unassigned)",
    description: "Is not assigned to a Technician or an installer to be worked."
  },
  pending_assigned: {
    label: "Pending (Assigned)",
    description: "Is assigned to a Technician or an installer to be worked."
  },
  accepted_pre_work: {
    label: "Accepted (Pre-Work)",
    description:
      "Technician has claimed the appointment and is preparing to start trip."
  },
  accepted: {
    label: "Accepted (Start Trip)",
    description:
      "Technician has started their trip to the location and is in transit."
  },
  running: {
    label: "Running",
    description:
      "The Technician has finished their trip and is currently working at the job site."
  },
  compeleted_lost_opportunity: {
    label: "Completed (Lost Opportunity)",
    description:
      "The work is completed, but it’s tied to a lost opportunity (Revenue on invoice)."
  },
  compeleted_won_opportunity: {
    label: "Completed (Won Opportunity)",
    description:
      "The work is completed, and is tied to a won opportunity (Revenue on invoice)."
  }
};

const open = ref(false);

const store = useStore();

const statusCustomizations: ComputedRef<{ [k: string]: CustomizationRecord }> =
  computed(() => {
    return store.getters.sortedAppointmentStatusCustomizations;
  });

function toggleOpen(): void {
  open.value = !open.value;
}

defineExpose(statusMap);
</script>

<style scoped lang="scss">
.color-pill {
  border-radius: 4px;
  min-width: 80px;
  width: 100%;
  height: 24px;
  flex-shrink: 0;
  &.icon-pill {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.7em;
  }
  &.img-bg {
    background-size: 50px;
    background-position-y: center;
  }
}
.v1 .color-pill {
  border-radius: 25px;
  width: 60px;
  height: 36px;
  &.img-bg {
    background-size: 65px;
  }
}
.hold {
  border: 2px dashed var(--neutral-gray-700);
  background: #e1e1e1;
  color: var(--font-body-color);
  display: flex;
  align-items: center;
  justify-content: center;
}
.example-text {
  font-size: 1.1em;
}
</style>
